/**
 * JSON-based key generation
 *
 * アンダースコア結合の曖昧性を解決するため、JSON配列としてキーを生成する。
 * 例: ["North", "2024-01"] -> '["North","2024-01"]'
 * 例: ["North_America", null] -> '["North_America",null]'
 */

import type { DataValue } from '../../../types/core.types';
import type { RowKey, ColumnKey, CellKey } from './types';

/**
 * 行キーを生成
 */
export function generateRowKey(values: DataValue[]): RowKey {
  return JSON.stringify(values);
}

/**
 * 列キーを生成
 */
export function generateColumnKey(values: DataValue[]): ColumnKey {
  return JSON.stringify(values);
}

/**
 * セルキーを生成
 * 形式: "row:<rowKey>:col:<colKey>"
 */
export function generateCellKey(rowKey: RowKey, colKey: ColumnKey): CellKey {
  return `row:${rowKey}:col:${colKey}`;
}

/**
 * 行キーをパース
 */
export function parseRowKey(key: RowKey): DataValue[] {
  return JSON.parse(key);
}

/**
 * 列キーをパース
 */
export function parseColumnKey(key: ColumnKey): DataValue[] {
  return JSON.parse(key);
}

/**
 * セルキーをパース
 */
export function parseCellKey(key: CellKey): { rowKey: RowKey; colKey: ColumnKey } {
  const match = key.match(/^row:(.*):col:(.*)$/);
  if (!match) {
    throw new Error(`Invalid cell key format: ${key}`);
  }
  return {
    rowKey: match[1],
    colKey: match[2]
  };
}

/**
 * 表示用の文字列を生成
 * NULL値は "NULL" として表示
 */
export function getDisplayString(values: DataValue[], separator: string = ' / '): string {
  return values.map(v => {
    if (v === null || v === undefined) return 'NULL';
    return String(v);
  }).join(separator);
}

/**
 * 表示用の値配列を生成
 */
export function getDisplayValues(values: DataValue[]): string[] {
  return values.map(v => {
    if (v === null || v === undefined) return 'NULL';
    return String(v);
  });
}
